import { useEffect, useState } from 'react'

export interface GeoPosition {
  lat: number
  lng: number
  accuracy: number | null
}

export interface UseGeolocationResult {
  position: GeoPosition | null
  error: string | null
  loading: boolean
  supported: boolean
}

/** Watches browser geolocation for Live Mode (Phase 2 M2). */
export function useGeolocation(enabled = true): UseGeolocationResult {
  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator
  const [position, setPosition] = useState<GeoPosition | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(enabled && supported)

  useEffect(() => {
    if (!enabled || !supported) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        })
        setLoading(false)
        setError(null)
      },
      (err) => {
        setLoading(false)
        setError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied'
            : 'Could not get your location',
        )
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 },
    )

    return () => navigator.geolocation.clearWatch(watchId)
  }, [enabled, supported])

  return { position, error, loading, supported }
}
